import { useState } from "react";
import { Autocomplete, Box, Button, Stack, TextField } from "@mui/material";
import { toast } from "react-toastify";
import UploadFile from "components/Input/UploadFile";
import { PAYMENT_METHOD_OPTIONS } from "./modulePaymentsConstants";
import { createEmptyDraft, toPaymentPayload, validatePaymentDraft } from "./modulePaymentsLogic";

const RecordPaymentForm = ({
  initialDraft = null,
  initialAttachments = [],
  requireMethod = false,
  canSelfVerify = false,
  submitting = false,
  onSubmit,
}) => {
  const [draft, setDraft] = useState(() => ({ ...createEmptyDraft(), ...(initialDraft || {}) }));
  const [upload, setUpload] = useState({ attachments: initialAttachments });
  const setField = (field) => (value) => setDraft((prev) => ({ ...prev, [field]: value }));

  const submit = () => {
    const error = validatePaymentDraft(draft, { requireMethod });
    if (error) {
      toast.error(error);
      return;
    }
    onSubmit(toPaymentPayload(draft, upload.attachments || []));
  };

  return (
    <Stack spacing={2}>
      <Box className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <TextField
          label="Amount"
          type="number"
          size="small"
          required
          value={draft.amount}
          onChange={(e) => setField("amount")(e.target.value)}
          inputProps={{ min: 0, step: "0.01" }}
        />
        <TextField
          label="Date"
          type="date"
          size="small"
          required
          InputLabelProps={{ shrink: true }}
          value={draft.date}
          onChange={(e) => setField("date")(e.target.value)}
        />
        <Autocomplete
          options={PAYMENT_METHOD_OPTIONS}
          value={PAYMENT_METHOD_OPTIONS.find((m) => m.value === draft.method) || null}
          onChange={(_, value) => setField("method")(value?.value || "")}
          getOptionLabel={(option) => option?.label || ""}
          renderInput={(params) => (
            <TextField {...params} label="Payment method" size="small" required={requireMethod} />
          )}
        />
        <TextField
          label="Reference / receipt #"
          size="small"
          value={draft.reference}
          onChange={(e) => setField("reference")(e.target.value)}
        />
        <TextField
          label="Notes"
          size="small"
          className="md:col-span-2"
          multiline
          minRows={2}
          value={draft.notes}
          onChange={(e) => setField("notes")(e.target.value)}
        />
      </Box>
      <UploadFile
        data={upload}
        setData={setUpload}
        imageKey="attachments"
        label="Payment proof / receipts"
        type="multiple"
        newDesign
      />
      <Stack direction="row" alignItems="center" justifyContent="space-between">
        {canSelfVerify ? (
          <label className="flex items-center gap-2 text-sm text-app-text-secondary">
            <input
              type="checkbox"
              checked={draft.autoVerify}
              onChange={(e) => setField("autoVerify")(e.target.checked)}
            />
            Add as already verified
          </label>
        ) : (
          <span />
        )}
        <Button variant="contained" onClick={submit} disabled={submitting}>
          {submitting ? "Submitting…" : "Submit payment"}
        </Button>
      </Stack>
    </Stack>
  );
};

export default RecordPaymentForm;
